
/**
 * @class
 * @function Pagination
 * Remembers loaded products and scroll position
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();

      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id="' + id + '"]');
      this.animations_enabled =
        document.body.classList.contains('animations-true') &&
        typeof gsap !== 'undefined';

      this.i = 2;
      this.restoring = false;

      this.storageKey =
        'pagination-state:' +
        id + ':' +
        location.pathname +
        location.search;

      this.savePosition = () => this.saveState();
    }

    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }

      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }

      window.addEventListener('pagehide', this.savePosition);

      const navigation = performance.getEntriesByType('navigation')[0];

      if (navigation && navigation.type === 'back_forward') {
        this.restoreState();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('pagehide', this.savePosition);

      if (this.observer) {
        this.observer.disconnect();
      }
    }

    saveState() {
      if (this.restoring) return;

      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            loadedPages: this.i - 2,
            scrollY: window.scrollY
          })
        );
      } catch (error) {
        console.warn('Could not save pagination state', error);
      }
    }

    async restoreState() {
      if (!this.grid) return;

      let saved;

      try {
        saved = JSON.parse(
          sessionStorage.getItem(this.storageKey) || 'null'
        );
      } catch (error) {
        return;
      }

      if (!saved || !saved.loadedPages) return;

      this.restoring = true;

      if (this.observer) {
        this.observer.disconnect();
      }

      try {
        for (let page = 0; page < saved.loadedPages; page++) {
          const success = await this.loadProducts();

          if (!success) break;
        }

        // Allow the product grid to finish rendering.
        await new Promise(resolve => setTimeout(resolve, 600));

        window.scrollTo({
          top: saved.scrollY || 0,
          behavior: 'instant'
        });
      } finally {
        this.restoring = false;

        if (
          this.observer &&
          this.isConnected &&
          this.hasAttribute('loading') === false
        ) {
          this.observer.observe(this);
        }
      }
    }

    addUrlParam(search, key) {
      const params = new URLSearchParams(search);
      params.set(key, this.i);

      return '?' + params.toString();
    }

    loadMore() {
      if (!this.button) return;

      this.button.addEventListener('click', event => {
        event.preventDefault();

        this.loadProducts();
        this.button.blur();
      });
    }

    infinite() {
      this.observer = new IntersectionObserver(entries => {
        if (
          !this.restoring &&
          entries[0].isIntersecting &&
          !this.hasAttribute('loading')
        ) {
          this.loadProducts();
        }
      }, {
        threshold: 1
      });

      this.observer.observe(this);
    }

    async loadProducts() {
      if (this.hasAttribute('loading') || !this.grid) {
        return false;
      }

      const url =
        location.pathname +
        this.addUrlParam(location.search, 'page');

      this.setAttribute('loading', 'true');

      try {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to load products');
        }

        const html = await response.text();

        const success = this.renderProducts(html, url);

        if (!success) {
          return false;
        }

        this.i++;

        dispatchCustomEvent('pagination:page-changed', {
          url: url
        });

        if (!this.restoring) {
          this.saveState();
        }

        return true;

      } catch (error) {
        console.error('Pagination error:', error);
        return false;

      } finally {
        this.removeAttribute('loading');
      }
    }

    renderProducts(html, url) {
      const parsed = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const grid_to_replace =
        parsed.getElementById('product-grid');

      if (!grid_to_replace) {
        if (this.observer) {
          this.observer.disconnect();
        }

        if (this.button) {
          this.button.classList.add('visually-hidden');
        }

        return false;
      }

      const products =
        grid_to_replace.querySelectorAll('.column');

      if (products.length === 0) {
        if (this.button) {
          this.button.classList.add('visually-hidden');
        }

        if (this.observer) {
          this.observer.disconnect();
        }

        return false;
      }

      products.forEach(product => {
        this.grid.appendChild(product);
      });

      if (this.animations_enabled && !this.restoring) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });

        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05
        });
      }

      return true;
    }
  }

  customElements.define('pagination-theme', Pagination);
}